import { rem } from 'polished'
import React, { useContext } from 'react'
import styled from 'styled-components'

import CardContainer from './Common/CardContainer'
import TopContent from './Common/TopContent'
import { ItemCardVariant } from './ItemCard'
import HeadingFullPrint from '../util/HeadingFullPrint'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { verticalChildSpacing } from 'components/utils/spacing'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import noop from 'lib/noop'
import BaseItemCard from 'tripPlanner/components/ItemCard/BaseItemCard'
import TripContext from 'tripPlanner/contexts/TripContext'
import { NoteItem } from 'tripPlanner/types/tripItem'
import { formatDateTimeOrDateRange } from 'tripPlanner/utils'

const NoteCardSection = styled.div`
  width: 100%;
  ${verticalChildSpacing[8]};
`

const NoteLinkSection = styled.div`
  display: flex;
  margin-top: ${rem(16)};
  color: ${({ theme }) => theme.palette.primary.normal};
  gap: ${rem(6)};
`

const NoteLink = styled.a`
  font-weight: ${({ theme }) => theme.font.primary.weight.bold};
  font-size: ${rem(13)};
  line-height: ${rem(18)};
  color: ${({ theme }) => theme.palette.primary.normal};
  overflow: hidden;
  text-overflow: ellipsis;
`

interface Props {
  item: NoteItem
  onClickTripName?: () => void
  variant: ItemCardVariant
}

function NoteItemCard({ item, onClickTripName = noop, variant }: Props) {
  const { notes, link, title } = item

  const timeFormat = useRegionTimeFormat()
  const tripContext = useContext(TripContext)

  const dateTimeLabel = formatDateTimeOrDateRange(item, timeFormat)

  return (
    <BaseItemCard item={item}>
      <CardContainer>
        <NoteCardSection>
          <TopContent item={item}>Note</TopContent>
          <VerticalSpacer gap={4}>
            {title && (
              <HeadingFullPrint lineClamp={2} variant="heading6">
                {title}
              </HeadingFullPrint>
            )}
            {notes && (
              <BodyText
                lineClamp={3}
                variant="large"
                weight="normal"
                wrap="pre-wrap"
              >
                {notes}
              </BodyText>
            )}
            {dateTimeLabel && (
              <BodyText variant="small" weight="normal">
                {dateTimeLabel}
              </BodyText>
            )}
          </VerticalSpacer>
          {link && (
            <NoteLinkSection>
              <NoteLink href={link} target="_blank" rel="nofollow">
                {link}
              </NoteLink>
            </NoteLinkSection>
          )}
          {variant === 'drawer' && tripContext && (
            <StructureLabel variant="information">
              <BodyTextBlock variant="small">Added to </BodyTextBlock>
              <TextLink
                size="caption"
                onClick={(e) => {
                  e.stopPropagation()
                  onClickTripName()
                }}
              >
                {tripContext.name}
              </TextLink>
            </StructureLabel>
          )}
        </NoteCardSection>
      </CardContainer>
    </BaseItemCard>
  )
}

export default NoteItemCard
